import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DataTable from "../DataTable";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, History, GitBranch, AlertTriangle } from "lucide-react";
import { fmtDate, naira } from "../../lib/format";

const BURG = { background: "#8B1E1E" };

export default function BomManager() {
  const { can } = useAuth();
  const editable = can("menu", "edit");
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState([]);
  const [active, setActive] = useState(null); // product row selected
  const [versions, setVersions] = useState([]);
  const [editor, setEditor] = useState(null); // {lines, effective_date}

  const load = () => { setLoading(true); api.get("/bom/products").then(setRows).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.get("/products?product_type=raw").then((r) => setIngredients(r)).catch(() => {});
    api.get("/products").then((all) => setIngredients(all.filter((p) => ["raw", "stock"].includes(p.product_type)))).catch(() => {});
  }, []);

  const openVersions = async (row) => {
    setActive(row);
    try { setVersions(await api.get(`/boms?product_id=${row.id}`)); } catch { setVersions([]); }
  };
  const startNew = () => setEditor({ lines: [{ product_id: "", quantity: 1 }], effective_date: new Date().toISOString().slice(0, 10) });
  const setLine = (i, k, v) => setEditor((e) => ({ ...e, lines: e.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setEditor((e) => ({ ...e, lines: [...e.lines, { product_id: "", quantity: 1 }] }));
  const rmLine = (i) => setEditor((e) => ({ ...e, lines: e.lines.filter((_, idx) => idx !== i) }));

  const saveVersion = async () => {
    const lines = editor.lines.filter((l) => l.product_id && Number(l.quantity) > 0).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) }));
    if (!lines.length) { toast.error("Add at least one ingredient"); return; }
    try {
      await api.post("/boms", { product_id: active.id, lines, effective_date: new Date(editor.effective_date).toISOString(), activate: true });
      toast.success("New recipe version activated"); setEditor(null); openVersions(active); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const activateVer = async (b) => { try { await api.post(`/boms/${b.id}/activate`); toast.success(`v${b.version} activated`); openVersions(active); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const setComboMode = async (row, mode) => { try { await api.put(`/bom/combo-mode/${row.id}`, { combo_mode: mode }); toast.success("Combo mode updated"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const cols = [
    { key: "name", label: "Product", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span> },
    { key: "product_type", label: "Type", render: (r) => <Badge variant="outline" className="capitalize">{r.product_type}</Badge> },
    { key: "category_name", label: "Category" },
    { key: "active_version", label: "Active Ver.", align: "right", render: (r) => r.active_version ? `v${r.active_version}` : "—" },
    { key: "line_count", label: "Ingredients", align: "right" },
    { key: "needs_bom", label: "Status", render: (r) => r.needs_bom
        ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded" style={{ background: "#F7E5E5", color: "#A52222" }}><AlertTriangle size={12} /> No BOM</span>
        : <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#EAF3EB", color: "#4F7A52" }}>{r.product_type === "combo" ? "Combo" : "Ready"}</span> },
    { key: "combo_mode", label: "Combo Mode", render: (r) => r.product_type !== "combo" ? "—" : (
      editable ? <Select value={r.combo_mode} onValueChange={(v) => setComboMode(r, v)}><SelectTrigger className="h-8 w-32" data-testid={`combo-mode-${r.id}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="recursive">Recursive</SelectItem><SelectItem value="flat">Flat bundle</SelectItem></SelectContent></Select>
      : <span className="capitalize">{r.combo_mode}</span>) },
    { key: "actions", label: "", sortable: false, render: (r) => r.product_type === "finished" && (
      <Button size="sm" variant="outline" onClick={() => openVersions(r)} data-testid={`bom-versions-${r.id}`}><History size={14} className="mr-1" /> Versions</Button>) },
  ];

  return (
    <div>
      <div className="mb-3 flex items-start gap-2 text-sm rounded-lg p-3" style={{ background: "#FBF3E2", color: "#7a5c2e", border: "1px solid #f0dcb4" }}>
        <GitBranch size={16} className="mt-0.5" /><span><b>Nested combos:</b> "Recursive" explodes each sub-product's recipe (deducts raw ingredients); "Flat bundle" deducts the finished sub-products as stock units. Set per combo above.</span>
      </div>
      <DataTable columns={cols} rows={rows} loading={loading} searchKeys={["name", "category_name"]} testIdPrefix="bom" emptyText="No products" />

      {/* Versions dialog */}
      <Dialog open={!!active && !editor} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">{active?.name} — Recipe Versions</DialogTitle>
            <DialogDescription>Historical sales use the version live at their sale time. Activating a version sets it as current.</DialogDescription></DialogHeader>
          <div className="space-y-3">{versions.map((b) => (
            <div key={b.id} className="rounded-lg border p-3" style={{ borderColor: "#ECE4D8", background: b.status === "active" ? "#F7FBF7" : "#FAF7F0" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><span className="font-semibold">v{b.version}</span>
                  <Badge variant="outline" className="capitalize" style={b.status === "active" ? { color: "#4F7A52", borderColor: "#cfe3d1" } : {}}>{b.status}</Badge>
                  <span className="text-xs" style={{ color: "#a58f7c" }}>Effective {fmtDate(b.effective_date)}</span></div>
                {editable && b.status !== "active" && <Button size="sm" variant="outline" onClick={() => activateVer(b)} data-testid={`activate-v${b.version}`}>Activate</Button>}
              </div>
              <div className="text-sm space-y-1">{(b.lines || []).map((l, i) => (
                <div key={i} className="flex justify-between"><span style={{ color: "#3B2722" }}>{l.product_name}</span>
                  <span style={{ color: "#765A4A" }}>{l.quantity} {l.unit} · {naira((l.cost || 0) * l.quantity)}</span></div>))}</div>
            </div>))}
            {!versions.length && <p className="text-sm" style={{ color: "#a58f7c" }}>No versioned recipe yet.</p>}
          </div>
          {editable && <DialogFooter><Button onClick={startNew} data-testid="bom-new-version" style={BURG} className="text-white"><Plus size={16} className="mr-1" /> New Version</Button></DialogFooter>}
        </DialogContent>
      </Dialog>

      {/* New version editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-brand">New Recipe Version — {active?.name}</DialogTitle>
            <DialogDescription>This becomes the active recipe from its effective date. Older sales keep their version.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 py-1"><Label>Effective date</Label>
            <Input type="date" value={editor?.effective_date || ""} onChange={(e) => setEditor((s) => ({ ...s, effective_date: e.target.value }))} data-testid="bom-effective-date" /></div>
          <div className="mt-2"><div className="flex items-center justify-between mb-2"><Label>Ingredients</Label>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="bom-add-line"><Plus size={14} className="mr-1" /> Add</Button></div>
            <div className="space-y-2">{editor?.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-8"><Select value={l.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
                  <SelectTrigger data-testid={`bom-line-${i}`}><SelectValue placeholder="Ingredient…" /></SelectTrigger>
                  <SelectContent>{ingredients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                <Input className="col-span-3" type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`bom-lineqty-${i}`} />
                <button className="col-span-1 flex justify-center" style={{ color: "#A52222" }} onClick={() => rmLine(i)}><Trash2 size={16} /></button>
              </div>))}</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={saveVersion} data-testid="bom-save-version" style={BURG} className="text-white">Activate Version</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
